/**
 * @module useContractAccess
 */

import {contractServiceType, PersonRole} from '~/utils/types/types'

export default (accessMap: AccessMap) => {
    const authStore = useAuthStore()
    const supplierStore = useSupplierStore()
    const {$conf} = useNuxtApp()

    /**
     *
     * Determines whether the "Availability" tab should be shown.
     *
     * **Condition:**
     *
     * - The tab will not be shown if:
     *   - The user is a supplier (`authStore.isSupplier`), and
     *   - The account configuration (`$conf.public.account.productsManagementForSuppliersHotelsRAOnly`)
     *     restricts product management to hotel rates and availability only.
     *
     * @function isShowContractAvailabilityTab: AccessFunction<'show.contract.availabilityTab'>
     */
    const isShowContractAvailabilityTab: AccessFunction<'show.contract.availabilityTab'> = () =>
        !(authStore.isSupplier && $conf.public.account.productsManagementForSuppliersHotelsRAOnly)

    /**
     * Access function for determining whether the main contract or reservations tab should be shown.
     *
     * **Condition**:
     * - The tab is shown if:
     *   - The user has the "TO1" role (`authStore.isTO1`), or
     *   - The "Availability" tab is accessible based on `isShowContractAvailabilityTab`.
     *
     * @function isShowContractTab: AccessFunction<'show.contract.informationTab' | 'show.contract.reservationsTab'>
     */
    const isShowContractTab: AccessFunction<'show.contract.informationTab' | 'show.contract.reservationsTab'> = () =>
        authStore.isTO1 || isShowContractAvailabilityTab()

    /**
     * Access function for determining whether the "Description" tab should be shown.
     *
     * **Condition**:
     * - The contract's `serviceType` must be included in the list of valid service types.
     * - The main contract tab must be accessible based on `isShowContractTab`.
     *
     * @function isShowContractDescriptionTab: AccessFunction<'show.contract.descriptionTab'>
     * @param {Object} contract - The contract-related data.
     * @param {contractServiceType} contract.serviceType - The type of service associated with the contract.
     */
    const isShowContractDescriptionTab: AccessFunction<'show.contract.descriptionTab'> = (contract?: {
        serviceType: contractServiceType
    }) => {
        if (!contract?.serviceType) return false
        const availableServices: string[] = [
            contractServiceType.ownExcursion,
            contractServiceType.concert,
            contractServiceType.ownTransfer,
            contractServiceType.ownCarRent,
            contractServiceType.ownCruise,
            contractServiceType.tour,
            contractServiceType.ownExtraService,
            contractServiceType.customComplexTour,
            contractServiceType.ownHotel,
            contractServiceType.ownInsurance,
            contractServiceType.ownVisa,
        ]
        return availableServices.includes(contract.serviceType) && isShowContractTab()
    }

    /**
     * Access function for determining whether the "Photo" tab should be shown.
     *
     * **Condition**:
     * - The contract's `serviceType` must be included in the list of valid service types.
     * - The main contract tab must be accessible based on `isShowContractTab`.
     *
     * @function isShowContractPhotosTab: AccessFunction<'show.contract.photoTab'>
     * @param {Object} contract - The contract-related data.
     * @param {contractServiceType} contract.serviceType - The type of service associated with the contract.
     */
    const isShowContractPhotosTab: AccessFunction<'show.contract.photoTab'> = (contract?: {
        serviceType: contractServiceType
    }) => {
        if (!contract?.serviceType) return false
        const availableServices: string[] = [
            contractServiceType.ownHotel,
            contractServiceType.ownExcursion,
            contractServiceType.concert,
            contractServiceType.ownTransfer,
            contractServiceType.ownCarRent,
            contractServiceType.ownCruise,
            contractServiceType.tour,
            contractServiceType.ownExtraService,
            contractServiceType.customComplexTour,
        ]
        return availableServices.includes(contract.serviceType) && isShowContractTab()
    }

    /**
     * Access function for determining whether the "Prices" tab should be shown.
     *
     * **Condition**:
     * - The contract's `serviceType` must be included in the list of valid service types.
     * - If the `serviceType` is `customComplexTour`, the supplier must have a price configuration of `STATIC`.
     *
     * @function isShowContractPricesTab: AccessFunction<'show.contract.pricesTab'>
     * @param {Object} contract - The contract-related data.
     * @param {contractServiceType} contract.serviceType - The type of service associated with the contract.
     */
    const isShowContractPricesTab: AccessFunction<'show.contract.pricesTab'> = (contract?: {
        serviceType: contractServiceType
    }) => {
        if (!contract?.serviceType) return false
        const availableServices: string[] = [
            contractServiceType.ownHotel,
            contractServiceType.ownCharter,
            contractServiceType.ownExcursion,
            contractServiceType.concert,
            contractServiceType.ownTransfer,
            contractServiceType.ownCarRent,
            contractServiceType.ownCruise,
            contractServiceType.ownExtraService,
            contractServiceType.ownInsurance,
            contractServiceType.ownVisa,
            contractServiceType.tour,
        ]
        if (
            contract.serviceType === contractServiceType.customComplexTour &&
            supplierStore.fullContract?.priceConfiguration === 'STATIC'
        ) {
            availableServices.push(contractServiceType.customComplexTour)
        }
        return availableServices.includes(contract.serviceType) && isShowContractTab()
    }

    /**
     * Access function for determining whether the "Rates and Availability" tab should be shown.
     *
     * **Condition**:
     * - The contract's `serviceType` must be valid.
     * - The "Availability" tab must be accessible based on `isShowContractAvailabilityTab`.
     *
     * @function isShowContractRatesAndAvailabilityTab: AccessFunction<'show.contract.ratesAndAvailabilityTab'>
     * @param {Object} contract - The contract-related data.
     * @param {contractServiceType} contract.serviceType - The type of service associated with the contract.
     */
    const isShowContractRatesAndAvailabilityTab: AccessFunction<'show.contract.ratesAndAvailabilityTab'> = (contract: {
        serviceType: contractServiceType
    }) => {
        if (!contract?.serviceType) return false
        return isShowContractAvailabilityTab()
            ? contract.serviceType === contractServiceType.ownHotel && $conf.public.products.editRatesAndAvailability
            : contract.serviceType === contractServiceType.ownHotel
    }

    /**
     * Access function for determining whether the "Business Intelligence" tab should be shown.
     *
     * **Condition**:
     * - The contract's `serviceType` must be valid for BI access.
     * - The user must have corporate access (`authStore.hasCorporateAccess`).
     * - The user's `personRole` must be one of the roles with BI permissions.
     *
     * @function isShowContractBITab: AccessFunction<'show.contract.BITab'>
     * @param {Object} contract - The contract-related data.
     * @param {contractServiceType} contract.serviceType - The type of service associated with the contract.
     */
    const isShowContractBITab: AccessFunction<'show.contract.BITab'> = (contract?: {
        serviceType: contractServiceType
    }) => {
        // ZSH FIX: Guard against undefined contract
        if (!contract?.serviceType) {
            return false
        }

        const productAvailable = [
            contractServiceType.ownExcursion,
            contractServiceType.ownCharter,
            contractServiceType.ownTransfer,
        ].includes(contract.serviceType)

        return (
            productAvailable &&
            $conf.public.account.hasBusinessIntelligenceAccess &&
            authStore.hasCorporateAccess &&
            [PersonRole.Supervisor, PersonRole.Director, PersonRole.Support].includes(authStore.personRole) &&
            isShowContractTab()
        )
    }

    /**
     * Access function for determining whether the "Provider Type" in the "Extra Service" tab should be shown.
     *
     * **Condition**:
     * - The "Provider Type" field is shown if:
     *   - The user is not a supplier (`!authStore.isSupplier`), and
     *   - The `formData` object includes the `providerType` property.
     *
     * @function isShowContractExtraServiceProviderType: AccessFunction<'show.contract.extraService.providerType'>
     * @param {Object} formData - The form data related to the contract.
     * @param {string} [formData.providerType] - The provider type field in the form data.
     */
    const isShowContractExtraServiceProviderType: AccessFunction<
        'show.contract.extraService.providerType'
    > = (formData: {providerType?: string}) =>
        !authStore.isSupplier && Object.prototype.hasOwnProperty.call(formData, 'providerType')

    /**
     * Access function for determining whether the "Price Discount Category" in the "Prices" tab should be shown.
     *
     * **Condition**:
     * - The "Price Discount Category" field is shown if:
     *   - The user is not a supplier (`!authStore.isSupplier`).
     *
     * @function isShowContractPriceDiscountCategory: AccessFunction<'show.contract.price.discountCategory'>
     */
    const isShowContractPriceDiscountCategory: AccessFunction<'show.contract.price.discountCategory'> = () =>
        !authStore.isSupplier

    accessMap.set('show.contract.informationTab', isShowContractTab)
    accessMap.set('show.contract.descriptionTab', isShowContractDescriptionTab)
    accessMap.set('show.contract.photoTab', isShowContractPhotosTab)
    accessMap.set('show.contract.pricesTab', isShowContractPricesTab)
    accessMap.set('show.contract.ratesAndAvailabilityTab', isShowContractRatesAndAvailabilityTab)
    accessMap.set('show.contract.availabilityTab', isShowContractAvailabilityTab)
    accessMap.set('show.contract.reservationsTab', isShowContractTab)
    accessMap.set('show.contract.BITab', isShowContractBITab)
    accessMap.set('show.contract.extraService.providerType', isShowContractExtraServiceProviderType)
    accessMap.set('show.contract.price.discountCategory', isShowContractPriceDiscountCategory)
}
